import { toast } from 'react-toastify';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import tassAxios from '@/apis/tass-api/axios';

type RoleRequest = {
  userId: string;
  organizationId: string;
  role: string;
};

type SDKAccessRequest = {
  userId: string;
  organizationId: string;
};

type MfaRequest = {
  userId: string;
  enabled: boolean;
  organizationId: string;
};

const assignRoleToUser = (userId: string, role: string) =>
  tassAxios.patch('/api/Admin/AssignRolesToUser', [role], { params: { userId } });

const removeRoleFromUser = (userId: string, role: string) =>
  tassAxios.patch('/api/Admin/RemoveRolesFromUser', [role], { params: { userId } });

const addSDKProductAccess = (userId: string) =>
  tassAxios.patch('/api/ApiUser/AddSDKProductAccess', null, { params: { userId } });

const removeSDKProductAccess = (userId: string) =>
  tassAxios.patch('/api/ApiUser/RemoveSDKProductAccess', null, { params: { userId } });

const updateMfaApi = (userId: string, enabled: boolean) =>
  tassAxios.patch('/api/Admin/UpdateMfa', null, { params: { userId, enabled } });

// UpdateUser overwrites the trial expiry, so send back the current value.
const updateUserApi = async (userId: string, firstName: string, lastName: string) => {
  const { data: user } = await tassAxios.get('/api/Admin/GetUser', { params: { id: userId } });
  return tassAxios.patch(
    '/api/Admin/UpdateUser',
    { userId, firstName, lastName, trialExpiresAt: user.trialExpiresAt },
    { params: { userId } }
  );
};

// DeleteUser is forbidden for non-superusers once the user has left the organization, so delete first.
const deleteUserApi = async (organizationId: string, userId: string) => {
  await tassAxios.delete('/api/Admin/DeleteUser', { params: { id: userId } });
  await tassAxios.patch('/api/Organization/RemoveUserFromOrganization', null, { params: { organizationId, userId } });
};

const resendSetPasswordEmailApi = (userId: string) =>
  tassAxios.post('/api/Admin/ResendSetPasswordEmail', null, { params: { userId } });

export const useUserActions = (organizationId: string) => {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['subscription', organizationId] });
    queryClient.invalidateQueries({ queryKey: ['product-accesses', organizationId] });
    queryClient.invalidateQueries({ queryKey: ['organization', organizationId] });
  };

  const { mutate: assignRole, isPending: assignRolePending } = useMutation({
    mutationFn: (vars: RoleRequest) => assignRoleToUser(vars.userId, vars.role),
    onSuccess: invalidate,
    onError: () => toast.error('Failed to assign role'),
  });

  const { mutate: removeRole, isPending: removeRolePending } = useMutation({
    mutationFn: (vars: RoleRequest) => removeRoleFromUser(vars.userId, vars.role),
    onSuccess: invalidate,
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: string } };
      if (axiosErr.response?.data === 'Full access cannot be assigned to users if the max seats are reached') {
        toast.error(axiosErr.response.data);
      } else {
        toast.error('Failed to remove role');
      }
    },
  });

  const { mutate: addSDK, isPending: addSDKPending } = useMutation({
    mutationFn: (vars: SDKAccessRequest) => addSDKProductAccess(vars.userId),
    onSuccess: invalidate,
    onError: () => toast.error('Failed to add SDK access'),
  });

  const { mutate: removeSDK, isPending: removeSDKPending } = useMutation({
    mutationFn: (vars: SDKAccessRequest) => removeSDKProductAccess(vars.userId),
    onSuccess: invalidate,
    onError: () => toast.error('Failed to remove SDK access'),
  });

  const { mutate: setMfa, isPending: mfaPending } = useMutation({
    mutationFn: (vars: MfaRequest) => updateMfaApi(vars.userId, vars.enabled),
    onSuccess: invalidate,
    onError: () => toast.error('Failed to update MFA requirement'),
  });

  const { mutate: updateUser, isPending: updateUserPending } = useMutation({
    mutationFn: (vars: { userId: string; firstName: string; lastName: string }) =>
      updateUserApi(vars.userId, vars.firstName, vars.lastName),
    onSuccess: invalidate,
    onError: () => toast.error('An error occurred while updating the user!'),
  });

  const { mutate: deleteUser, isPending: deleteUserPending } = useMutation({
    mutationFn: (userId: string) => deleteUserApi(organizationId, userId),
    onSettled: invalidate,
    onError: () => toast.error('An error occurred while deleting the user!'),
  });

  const { mutate: resendSetPasswordEmail, isPending: resendSetPasswordEmailPending } = useMutation({
    mutationFn: resendSetPasswordEmailApi,
    onSuccess: () => toast.info('Set password email successfully sent!'),
    onError: () => toast.error('An error occurred while sending the set password email!'),
  });

  const updateAdminSettings = (userId: string, assign: boolean) => {
    if (assign) {
      assignRole({ userId, organizationId, role: 'Admin' });
    } else {
      removeRole({ userId, organizationId, role: 'Admin' });
    }
  };

  const updateUserSDKAccess = (userId: string, hasAccess: boolean) => {
    if (hasAccess) {
      addSDK({ userId, organizationId });
    } else {
      removeSDK({ userId, organizationId });
    }
  };

  const updateUserMfaRequirement = (userId: string, enabled: boolean) => {
    setMfa({ userId, enabled, organizationId });
  };

  return {
    updateAdminSettings,
    updateUserSDKAccess,
    updateUserMfaRequirement,
    updateUser,
    deleteUser,
    resendSetPasswordEmail,
    inProgress:
      assignRolePending ||
      removeRolePending ||
      addSDKPending ||
      removeSDKPending ||
      mfaPending ||
      updateUserPending ||
      deleteUserPending ||
      resendSetPasswordEmailPending,
  };
};
